import React from 'react';
import { HStack, Button, Text, Spinner, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export interface LocaleOption {
  code: string;
  label: string;
  flag: string;
}

export interface LocalePickerProps {
  currentLocale: string;
  availableLanguages: LocaleOption[];
  onLocaleChange: (code: string) => void;
  isSyncing?: boolean;
  isLoading?: boolean;
}

export function LocalePicker({
  currentLocale,
  availableLanguages,
  onLocaleChange,
  isSyncing = false,
  isLoading = false,
}: LocalePickerProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <HStack spacing={3} justify="center">
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.500">{t('common.loading')}</Text>
      </HStack>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      <HStack spacing={3} flexWrap="wrap">
        {availableLanguages.map((lang) => {
          const isActive = currentLocale === lang.code;
          return (
            <Button
              key={lang.code}
              onClick={() => onLocaleChange(lang.code)}
              isDisabled={isSyncing}
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'blue' : 'gray'}
              size="md"
              borderRadius="lg"
              leftIcon={<Text fontSize="lg">{lang.flag}</Text>}
            >
              {lang.label}
            </Button>
          );
        })}
      </HStack>
      {isSyncing && (
        <HStack spacing={2}>
          <Spinner size="xs" />
          <Text fontSize="xs" color="gray.500">{t('common.saving')}</Text>
        </HStack>
      )}
    </VStack>
  );
}

export default LocalePicker;
